import { useState } from "react";
import { Reveal } from "@components/ui";

interface GuidelineCategory {
  title: string;
  icon: React.ReactNode;
  bullets: string[];
}

const categories: GuidelineCategory[] = [
  {
    title: "In the Kitchen",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <path d="M12 22c3.5 0 6-2.5 6-6 0-4-3-7-6-10-3 3-6 6-6 10 0 3.5 2.5 6 6 6z" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 16a2 2 0 104 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    bullets: [
      "Keep burners clean and free of grease buildup",
      "Ensure blue flame, not yellow (yellow = incomplete combustion)",
      "Do not leave cooking unattended",
      "Keep flammable items away (cloth, tissue, oils)",
      "Never hang clothes or objects above gas stoves",
    ],
  },
  {
    title: "Gas Cylinders",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <rect x="6" y="4" width="12" height="16" rx="3" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 4V2h4v2M6 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    bullets: [
      "Store cylinders upright in well-ventilated areas",
      "Keep away from heat sources and direct sunlight",
      "Check regulators and hoses regularly for wear",
      "Never use damaged or expired cylinders",
      "Ensure proper valve closure when not in use",
    ],
  },
  {
    title: "Piped Gas Systems",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <path d="M4 10h16M4 14h16M8 10V6M16 14v4M12 10v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    bullets: [
      "Do not tamper with gas meters or pipelines",
      "Report any visible damage to pipes immediately",
      "Keep gas meter areas accessible for inspections",
      "Do not hang objects on gas pipes",
      "Ensure all connections are professionally installed",
    ],
  },
  {
    title: "Smart Meters",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
        <rect x="5" y="3" width="14" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M9 7h6M9 11h6M9 15h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    bullets: [
      "Monitor your gas usage through the GASIS portal",
      "Set up alerts for unusual consumption patterns",
      "Report meter malfunctions immediately",
      "Keep the meter area clean and accessible",
      "Do not attempt to repair or adjust the smart meter",
    ],
  },
];

export function PreventiveGuidelinesSection() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="w-full bg-[#f5f5f7] overflow-hidden px-5 md:px-10 lg:px-[180px] py-12 md:py-16 lg:py-[100px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-10 md:gap-14 lg:gap-16">
        {/* Title */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 max-w-[1112px] mx-auto text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#269d0e]/10 text-[#269d0e] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Prevention
            </span>
            <h2 className="text-[28px] md:text-[44px] lg:text-[54px] font-semibold tracking-[-0.04em] text-[#122d63] leading-[1.1]">
              Preventive Safety Guidelines
            </h2>
            <p className="text-[14px] md:text-base lg:text-[22px] tracking-[-0.04em] text-[#62636c] max-w-[800px]">
              Follow these essential guidelines to keep your home and business
              safe from gas hazards.
            </p>
          </div>
        </Reveal>

        {/* Accordion */}
        <Reveal direction="up" className="w-full">
          <div className="flex flex-col gap-3 md:gap-4">
            {categories.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={index}
                  className={`rounded-[20px] overflow-hidden transition-all duration-300 ${
                    isOpen
                      ? "bg-white shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
                      : "bg-white hover:shadow-[0_2px_16px_rgba(0,0,0,0.06)]"
                  }`}
                >
                  {/* Header */}
                  <button
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    className="w-full flex items-center gap-4 md:gap-5 p-5 md:p-6 lg:p-7 cursor-pointer"
                  >
                    {/* Icon */}
                    <div
                      className={`w-11 h-11 md:w-13 md:h-13 lg:w-14 lg:h-14 rounded-[14px] flex items-center justify-center shrink-0 transition-all duration-300 ${
                        isOpen
                          ? "bg-[#122d63] text-white"
                          : "bg-[#122d63]/8 text-[#122d63]"
                      }`}
                    >
                      {item.icon}
                    </div>
                    {/* Title + Number */}
                    <div className="flex-1 flex items-center gap-3 min-w-0">
                      <h3 className="text-left text-[17px] md:text-[22px] lg:text-[26px] font-semibold tracking-[-0.04em] text-[#1e1f24] truncate">
                        {item.title}
                      </h3>
                      <span className="hidden md:inline text-[13px] text-[#62636c]/50 font-medium shrink-0">
                        {item.bullets.length} guidelines
                      </span>
                    </div>
                    {/* Arrow */}
                    <div
                      className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                        isOpen ? "bg-[#122d63] rotate-180" : "bg-[#f0f1f3]"
                      }`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 md:w-5 md:h-5">
                        <path
                          d="M6 9l6 6 6-6"
                          stroke={isOpen ? "white" : "#62636c"}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </button>

                  {/* Content */}
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 md:px-6 lg:px-7 pb-6 md:pb-7">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-3 ml-0 md:ml-[60px] lg:ml-[72px]">
                          {item.bullets.map((bullet, bIndex) => (
                            <div
                              key={bIndex}
                              className="flex items-start gap-3 p-3 md:p-3.5 rounded-[12px] bg-[#f8f9fb]"
                            >
                              <div className="w-5 h-5 rounded-full bg-[#269d0e]/15 flex items-center justify-center shrink-0 mt-0.5">
                                <svg viewBox="0 0 12 12" className="w-3 h-3">
                                  <path d="M10 3L4.5 8.5 2 6" stroke="#269d0e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>
                              <p className="text-[13px] md:text-[15px] lg:text-[17px] tracking-[-0.03em] text-[#4a4b52] leading-[1.5]">
                                {bullet}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
